import { createRouter, createWebHistory } from "vue-router";
import { useCookies } from "vue3-cookies";
import { useOrgStore } from "@/stores/org";
import { useAuthStore } from "@/stores/auth";
import apiClient from "@/config/apiClient";

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/",
      name: "home",
      component: () => import("../views/HomeView.vue"),
    },
    {
      path: "/admin/location",
      name: "location",
      component: () => import("@/views/admin/event/location/LocationIndex.vue"),
    },
    {
      path: "/admin/location/:id",
      name: "locationShow",
      component: () => import("@/views/admin/event/location/LocationShow.vue"),
    },
    {
      path: "/admin/location/:id/update",
      name: "locationEdit",
      component: () => import("@/views/admin/event/location/LocationEdit.vue"),
    },
    {
      path: "/admin/location/create",
      name: "locationCreate",
      component: () => import("@/views/admin/event/location/LocationCreate.vue"),
    },
    {
      path: "/admin/question",
      name: "question",
      component: () => import("@/views/admin/question/QuestionIndex.vue"),
    },
    {
      path: "/admin/question/create",
      name: "questionCreate",
      component: () => import("@/views/admin/question/QuestionCreate.vue"),
    },
    {
      path: "/admin/question/:id/update",
      name: "questionEdit",
      component: () => import("@/views/admin/question/QuestionEdit.vue"),
    },
    {
      path: "/admin/event",
      name: "EventIndex",
      component: () => import("@/views/admin/event/EventIndex.vue"),
    },
    {
      path: "/admin/event/create",
      name: "EventCreate",
      component: () => import("@/views/admin/event/EventCreate.vue"),
    },
    {
      path: "/admin/event/:id",
      name: "EventShow",
      component: () => import("@/views/admin/event/EventShow.vue"),
    },
    {
      path: "/admin/event/:id/update",
      name: "EventEdit",
      component: () => import("@/views/admin/event/EventEdit.vue"),
    },
    {
      path: "/admin/event/:id/participant-form",
      name: "FormIndex",
      component: () => import("@/views/admin/event/participant_form/FormIndex.vue"),
    },
    {
      path: "/admin/event/:id/package",
      name: "PackageIndex",
      component: () => import("@/views/admin/event/package/PackageIndex.vue"),
    },
    {
      path: "/admin/event/:id/create-participant",
      name: "ParticipantCreate",
      component: () => import("@/views/admin/event/participant/ParticipantCreate.vue"),
    },
    {
      path: "/admin/event/:id/participant/:partId",
      name: "ParticipantShow",
      component: () => import("@/views/admin/event/participant/ParticipantShow.vue"),
    },
    {
      path: "/admin/event/:id/participant/:partId/update-participant",
      name: "ParticipantEdit",
      component: () => import("@/views/admin/event/participant/ParticipantEdit.vue"),
    },
    {
      path: "/admin/event/:id/team",
      name: "TeamEditVue",
      component: () => import("@/views/admin/event/team/TeamEdit.vue"),
    },
    {
      path: "/admin/event/:id/team-participant",
      name: "ParticipantTeamEditVue",
      component: () => import("@/views/admin/event/participant_team/ParticipantTeamEdit.vue"),
    },
    {
      path: "/admin/event/:id/room-participant",
      name: "RoomEditVue",
      component: () => import("@/views/admin/event/participant_room/RoomEdit.vue"),
    },
    {
      path: "/admin/event/:id/promo/:promoId",
      name: "PromoShowVue",
      component: () => import("@/views/admin/event/promo/PromoShow.vue"),
    },
    {
      path: "/admin/event/:id/promo/:promoId/update-promo",
      name: "PromoEditVue",
      component: () => import("@/views/admin/event/promo/PromoEdit.vue"),
    },
    {
      path: "/admin/event/:id/create-promo",
      name: "PromoCreateVue",
      component: () => import("@/views/admin/event/promo/PromoCreate.vue"),
    },
    {
      path: "/admin/event/:id/usher/:usherId",
      name: "UsherShowVue",
      component: () => import("@/views/admin/event/usher/UsherShow.vue"),
    },
    {
      path: "/admin/event/:id/usher/:usherId/update-usher",
      name: "UsherEditVue",
      component: () => import("@/views/admin/event/usher/UsherEdit.vue"),
    },
    {
      path: "/admin/event/:id/create-usher",
      name: "UsherCreateVue",
      component: () => import("@/views/admin/event/usher/UsherCreate.vue"),
    },
    {
      path: "/login",
      name: "login",
      component: () => import("@/views/auth/AdminLogin.vue"),
      meta: { public: true, requiresOrg: false },
    },
    {
      path: "/register",
      name: "Register",
      component: () => import("@/views/Register.vue"),
      // public: true is required for the existing guard (it gates on
      // `meta.public`, not `meta.requiresAuth`). The other two keys are
      // documentation that this is the prompt-specified meta.
      meta: { public: true, requiresAuth: false, requiresOrg: false },
    },
    {
      path: "/accept-invite/:token",
      name: "AcceptInvite",
      component: () => import("@/views/AcceptInvite.vue"),
      // Same public bypass as Register — the token in the URL is the auth
      // proof, so this must be reachable with no cookie at all.
      meta: { public: true, requiresAuth: false, requiresOrg: false },
    },
    {
      path: "/verify-email",
      name: "VerifyEmail",
      component: () => import("@/views/VerifyEmail.vue"),
      meta: {
        requiresAuth: true,
        requiresOrg: false,
        requiresVerified: false,
        requiresOnboarding: false,
      },
    },
    {
      path: "/onboarding",
      name: "Onboarding",
      component: () => import("@/views/Onboarding.vue"),
      meta: {
        requiresAuth: true,
        requiresOrg: false,
        requiresVerified: true,
        requiresOnboarding: false,
      },
    },
    {
      path: "/superadmin",
      name: "SuperadminDashboard",
      component: () =>
        import("@/views/superadmin/SuperadminDashboard.vue"),
      meta: {
        requiresAuth: true,
        requiresOrg: false,
        requiresVerified: true,
        requiresOnboarding: false,
        isSuperadminRoute: true,
      },
    },
    {
      path: "/platform/billing",
      name: "PlatformBilling",
      component: () =>
        import("@/views/platform/PlatformBilling.vue"),
      meta: {
        requiresAuth: true,
        requiresOrg: false,
        requiresVerified: true,
        requiresOnboarding: false,
        isPlatformAdminRoute: true,
      },
    },
    {
      path: "/platform/events",
      name: "PlatformEvents",
      component: () =>
        import("@/views/platform/PlatformEvents.vue"),
      meta: {
        requiresAuth: true,
        requiresOrg: false,
        requiresVerified: true,
        requiresOnboarding: false,
        isPlatformAdminRoute: true,
      },
    },
    {
      path: "/billing",
      name: "Billing",
      component: () => import("@/views/admin/Billing.vue"),
      // No explicit meta.public/requiresOrg/etc. — defaults to "auth+org+
      // verified+onboarded required" via the Phase 5.6 guard, which is
      // exactly what we want for an org-scoped billing page.
      meta: { requiresAuth: true },
    },
    {
      path: "/select-org",
      name: "SelectOrg",
      component: () => import("@/views/OrgSelect.vue"),
      meta: { requiresAuth: true, requiresOrg: false },
    },
    {
      path: "/settings/organization",
      name: "OrgSettings",
      component: () => import("@/views/admin/OrgSettings.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/profile",
      name: "Profile",
      component: () => import("@/views/Profile.vue"),
      // User-level page (name/email/org list), not scoped to a single org —
      // no reason to force org-select first.
      meta: { requiresAuth: true, requiresOrg: false },
    },
    {
      path: "/member/",
      name: "member",
      component: () => import("@/views/admin/member/MemberIndex.vue"),
    },
    {
      path: "/member/:id",
      name: "memberShow",
      component: () => import("@/views/admin/member/MemberShow.vue"),
    },
    {
      path: "/cellGroup/",
      name: "cellGroup",
      component: () => import("@/views/admin/cellGroup/cellGroupIndex.vue"),
    },
    {
      path: "/cellGroup/:id",
      name: "cellGroupShow",
      component: () => import("@/views/admin/cellGroup/cellGroupShow.vue"),
    },
    {
      path: "/cellGroup/update/:id",
      name: "cellGroupUpdate",
      component: () => import("@/views/admin/cellGroup/cellGroupUpdate.vue"),
    },
    {
      path: "/cellGroup/create",
      name: "cellGroupCreate",
      component: () => import("@/views/admin/cellGroup/cellGroupCreate.vue"),
    },
    {
      path: "/about",
      name: "about",
      component: () => import("../views/AboutView.vue"),
    },
  ],
});

router.beforeEach(async (to) => {
  const { cookies } = useCookies();
  const isAuthenticated = !!cookies.get("Convena-AccessToken");

  // --- 1. Auth check (Phase 3.1, unchanged) ---
  if (!to.meta.public && !isAuthenticated) {
    return { name: "login" };
  }

  // Short-circuit: authed users on /login or /register go home.
  if ((to.name === "login" || to.name === "Register") && isAuthenticated) {
    return { name: "home" };
  }

  // Public routes bypass every authed check below.
  if (to.meta.public) {
    return;
  }

  // --- Hydrate authStore.user once on initial boot ---
  // The guard fires BEFORE App.vue's onMounted, so authStore.user is empty
  // on a fresh load (hard refresh, email-link click → backend redirect).
  // Without this, the email-verified check below would always fail on
  // first navigation. We fetch once when there's a cookie but no user id;
  // subsequent navigations reuse the populated store.
  const authStore = useAuthStore();
  if (isAuthenticated && !authStore.user?.id) {
    try {
      const resp = await apiClient.get("/api/userAdmin");
      authStore.user = resp.data.user;
      authStore.roles = resp.data.roles;
      authStore.permissions = resp.data.permissions;
    } catch (e) {
      console.log("userAdmin hydration failed in route guard", e);
    }
  }

  // --- 2. Email verification check (Phase 5.6) ---
  if (to.meta.requiresVerified !== false && !authStore.isEmailVerified) {
    if (to.name !== "VerifyEmail") {
      return { name: "VerifyEmail" };
    }
    // Already on VerifyEmail — let it render.
    return;
  }

  // --- 2.5. Superadmin carve-out (Phase 6.3) ---
  // Superadmins heading to a superadmin-only route skip the org-context and
  // onboarding gates below. Without this, a platform operator with no org of
  // their own would be forced into SelectOrg/Onboarding before reaching the
  // panel. Email verification is still required (handled above).
  if (authStore.isSuperadmin && to.meta.isSuperadminRoute) {
    return;
  }

  // --- 2.6. Platform-admin carve-out ---
  // Same reasoning as the superadmin carve-out above, for the platform_admin
  // role (billing verification) — a platform_admin account may have no org
  // of its own either.
  if (authStore.isPlatformAdmin && to.meta.isPlatformAdminRoute) {
    return;
  }

  // --- 3. Org context check (Phase 3.3, gated by requiresOrg) ---
  const orgStore = useOrgStore();
  if (to.meta.requiresOrg !== false) {
    if (!orgStore.currentOrg) {
      // First try to restore from localStorage. On hard refresh this guard
      // fires before App.vue's onMounted, so we can't rely on App.vue having
      // already called restoreOrg. restoreOrg() fetches userOrgs internally
      // if needed and is a no-op when there's no stored id.
      await orgStore.restoreOrg();
      if (!orgStore.currentOrg) {
        if (orgStore.userOrgs.length === 0) {
          try {
            await orgStore.fetchUserOrgs();
          } catch (e) {
            console.log("fetchUserOrgs failed in route guard", e);
          }
        }
        if (orgStore.userOrgs.length === 1) {
          orgStore.selectOrg(orgStore.userOrgs[0]);
        } else {
          return { name: "SelectOrg" };
        }
      }
    }
  }

  // --- 4. Onboarding check (Phase 5.6) ---
  // Only redirect when the field is *explicitly* false — undefined leaves the
  // user alone (covers legacy orgs and routes that don't carry an org).
  if (
    to.meta.requiresOnboarding !== false &&
    orgStore.currentOrg &&
    orgStore.currentOrg.onboarding_completed === false
  ) {
    if (to.name !== "Onboarding") {
      return { name: "Onboarding" };
    }
  }
});

export default router;
